import { z } from 'zod';
import { AiReasoning } from './ai-policy.js';
/**
 * Key AI và bảng giá — docs/01-tech-stack.md §4, luật #4 của CLAUDE.md.
 *
 * ⚠️ Không schema nào ở đây chứa key thật. API **không bao giờ** trả nó, kể cả
 * cho chủ của chính key đó (docs/security.md §6). Chỉ `keyLast4`.
 *
 * ## Key thuộc về NGƯỜI DÙNG, không thuộc về hệ thống (APM-805)
 *
 * Mỗi người tự nhập key của mình, và **ai nhập key thì người đó dùng được AI**.
 * Không có key cấp tổ chức, không có `SUPER_ADMIN` nhập một key cho cả site.
 *
 * Ba hệ quả cố ý:
 *
 * 1. **Chi phí về đúng người tiêu.** Hoá đơn của mỗi người nằm ở tài khoản
 *    provider của chính họ; `ai_usage_log` vẫn ghi đủ để tổ chức xem tổng.
 * 2. **AI là tuỳ chọn ở mức từng người**, không phải mức hệ thống — đúng tinh
 *    thần ADR-006. Người chưa nhập key vẫn dùng được mọi thứ, chỉ là làm tay.
 * 3. **Không có lời gọi AI nào không có chủ.** Điều này kiểm được: mọi đường
 *    gọi AI trong repo đều xuất phát từ một request của người dùng (job nền
 *    tính tín hiệu rủi ro bằng code thuần, không gọi model).
 */
/**
 * `litellm` là một **gateway**, không phải một nhà cung cấp model.
 *
 * Nó nhận đúng định dạng Anthropic Messages ở `/v1/messages` rồi chuyển tiếp
 * tới model nào cũng được. Vì vậy nó dùng lại nguyên SDK Anthropic, chỉ khác
 * `baseUrl` — xem `modules/ai/providers/` để biết vì sao không viết một tầng
 * dịch OpenAI riêng.
 *
 * `openai` và `gemini` (APM-AIP) gọi thẳng nhà cung cấp bằng key của chính
 * người dùng. Hai nhà này **không** nói định dạng Anthropic, nên chúng đi qua
 * tầng dịch `providers/openai-compat-client.ts` — Gemini dùng endpoint tương
 * thích OpenAI của Google. Địa chỉ cố định, không nhập `baseUrl`.
 */
export const AiProvider = z.enum(['anthropic', 'litellm', 'openai', 'gemini']);
const PROVIDER_HOST = {
    anthropic: 'Anthropic luôn gọi api.anthropic.com',
    openai: 'OpenAI luôn gọi api.openai.com',
    gemini: 'Gemini luôn gọi generativelanguage.googleapis.com',
};
export const AiProviderKeyInput = z
    .object({
    provider: AiProvider.default('anthropic'),
    /** Nhãn để phân biệt nhiều key của cùng một người, vd "Cá nhân", "Công ty". */
    label: z.string().min(1).max(100),
    apiKey: z.string().min(1, 'API key là bắt buộc').max(255),
    /**
     * Địa chỉ gateway, chỉ cho `litellm`. Không có đuôi `/v1` — SDK tự thêm.
     *
     * Thông điệp lỗi tự viết chứ không dùng mặc định của Zod ("Invalid URL"):
     * lỗi thường gặp nhất là gõ thiếu `https://`, và câu tiếng Anh kia không
     * nói được điều đó.
     */
    baseUrl: z
        .url({ message: 'Địa chỉ phải là URL đầy đủ, ví dụ https://litellm.noi-bo.vn' })
        .max(300)
        .optional(),
})
    .superRefine((value, ctx) => {
    if (value.provider === 'litellm' && !value.baseUrl) {
        ctx.addIssue({
            code: 'custom',
            path: ['baseUrl'],
            message: 'Gateway LiteLLM phải có địa chỉ. Ví dụ: https://litellm.noi-bo.vn',
        });
    }
    // Chặn `baseUrl` cho provider có địa chỉ cố định thay vì lặng lẽ bỏ qua: một
    // trường được điền mà không có tác dụng là một trường sẽ bị hiểu sai.
    if (value.provider !== 'litellm' && value.baseUrl) {
        ctx.addIssue({
            code: 'custom',
            path: ['baseUrl'],
            message: `Chỉ gateway LiteLLM cần địa chỉ. Key ${PROVIDER_HOST[value.provider]}.`,
        });
    }
});
/**
 * Một model cùng thông tin để người dùng chọn — APM-805.
 *
 * `priced` là thứ quyết định model đó có dùng được hay không: `AiGateway` từ
 * chối gọi model không có dòng giá (luật #4 — cost ghi 0 là mọi báo cáo im lặng
 * nói dối), nên một model chưa có giá sẽ bị **bỏ qua** trong chuỗi ưu tiên.
 * Nói ra ở đây để người dùng không chọn xong rồi mới phát hiện.
 */
export const AiModelOption = z.object({
    id: z.string(),
    /** Có dòng giá trong `ai_model_pricing` tại thời điểm hỏi. */
    priced: z.boolean(),
    /**
     * Model **không sinh được text** (tts, audio, ảnh, embedding, computer-use)
     * hoặc là wildcard của gateway (`anthropic/*`). Không đưa vào gợi ý và cảnh
     * báo nếu người dùng tự chọn.
     */
    usable: z.boolean(),
});
/**
 * Chuỗi model **có thứ tự ưu tiên** cho từng tầng — APM-805.
 *
 * ## Vì sao là chuỗi, không phải một model
 *
 * Ba tình huống thật mà một model duy nhất không chịu được:
 *
 * 1. **Hết quota / 429.** Gateway nội bộ dùng chung cả tổ chức; giờ cao điểm
 *    thì model tốt nhất là model bị giới hạn trước tiên.
 * 2. **Model bị gỡ.** Danh sách của một gateway đổi theo tuần; một model
 *    hardcode biến mất là tính năng chết, không phải chậm.
 * 3. **Chưa có bảng giá.** Model không có giá bị bỏ qua chứ không làm lời gọi
 *    thất bại — chuỗi vẫn chạy tiếp.
 *
 * Thứ tự là **ưu tiên**: thử phần tử đầu, lỗi tạm thời thì rơi xuống phần tử
 * sau. Lỗi *không* rơi xuống: sai key (401), vượt hạn mức của ta, và model từ
 * chối nội dung — thử model khác ở ba ca đó là đi tìm một câu "vâng" khác,
 * không phải chịu lỗi hạ tầng.
 *
 * Danh sách rỗng = dùng cấu hình tầng của tổ chức (`ai.model.*`).
 */
export const AiModelChains = z.object({
    strong: z.array(z.string().min(1).max(120)).max(5),
    standard: z.array(z.string().min(1).max(120)).max(5),
    small: z.array(z.string().min(1).max(120)).max(5),
    /**
     * Mức suy nghĩ cho từng loại việc — APM-MDL-d. Thay mức **mặc định của tầng**
     * (`REASONING_DEFAULT_BY_TIER`); skill tự khai `constraints.reasoning` vẫn
     * thắng, trần tổ chức vẫn kẹp. Bỏ trống = mặc định của tầng.
     */
    effort: z
        .object({
        strong: AiReasoning.optional(),
        standard: AiReasoning.optional(),
        small: AiReasoning.optional(),
    })
        .optional(),
});
export const EMPTY_MODEL_CHAINS = { strong: [], standard: [], small: [] };
export const UpdateAiModelChainsInput = z.object({ models: AiModelChains });
/**
 * Một dòng benchmark **công khai** của một model — APM-MDL-d.
 *
 * Nguồn: Artificial Analysis (`/api/v2/data/llms/models`). Điều khoản dùng
 * miễn phí **bắt buộc ghi nguồn** — mọi màn hình hiện những con số này phải
 * hiện "Nguồn: Artificial Analysis". Chỉ số là thang của nguồn (≈ 0–100), không
 * so trực tiếp với tỉ lệ đạt golden nội bộ.
 */
export const AiModelBenchmark = z.object({
    source: z.literal('artificial_analysis'),
    slug: z.string(),
    name: z.string(),
    creator: z.string().nullable(),
    /** Mức suy nghĩ suy từ tên/slug của biến thể (`(high)`, `-thinking`…); `null` = mặc định. */
    reasoning: AiReasoning.nullable(),
    intelligenceIndex: z.number().nullable(),
    codingIndex: z.number().nullable(),
    mathIndex: z.number().nullable(),
    /** Giá công khai mỗi 1M token của hãng — để **đối chiếu**, không để tính tiền (luật #4). */
    priceInput1m: z.number().nullable(),
    priceOutput1m: z.number().nullable(),
    priceBlended1m: z.number().nullable(),
    outputTokensPerSecond: z.number().nullable(),
    fetchedAt: z.iso.datetime(),
});
/** Nguồn benchmark công khai do SUPER_ADMIN cấu hình — APM-MDL-d. Key chỉ lộ 4 ký tự cuối. */
export const AiBenchmarkSourceView = z.object({
    provider: z.literal('artificial_analysis'),
    keyLast4: z.string().length(4),
    isActive: z.boolean(),
    lastFetchedAt: z.iso.datetime().nullable(),
    lastError: z.string().nullable(),
    /** Số dòng benchmark đang có. */
    modelCount: z.number().int().nonnegative(),
});
export const AiBenchmarkSourceInput = z.object({
    apiKey: z.string().trim().min(8).max(300),
});
/**
 * Đề xuất cấu hình cho một loại việc — APM-MDL-d. **Chỉ là đề xuất** (luật #6):
 * người dùng bấm áp dụng mới đổi chuỗi và effort của key.
 */
export const AiTierRecommendation = z.object({
    tier: z.enum(['strong', 'standard', 'small']),
    /** Id đúng như gateway gọi — đã là **đường rẻ nhất** trong các đường của cùng model. */
    modelId: z.string(),
    reasoning: AiReasoning.nullable(),
    /** Điểm dùng để chọn: tỉ lệ đạt golden ×100 (`internal`) hoặc chỉ số công khai (`public`). */
    score: z.number(),
    basis: z.enum(['internal', 'public']),
    /** Giá theo **bảng giá của ta** (luật #4), USD mỗi 1M token; blended = (3 × vào + ra) / 4. */
    price: z.object({ input1m: z.number(), output1m: z.number(), blended1m: z.number() }),
    /** Giá công khai của cùng model — lệch nhiều so với `price` là dấu hiệu bảng giá cần xem lại. */
    publicBlended1m: z.number().nullable(),
    /** Các đường khác của **cùng** model trên gateway, kèm giá — để thấy vì sao đường này rẻ nhất. */
    routes: z.array(z.object({ modelId: z.string(), blended1m: z.number() })),
    reason: z.string(),
});
/**
 * Chuỗi model theo đời model — APM-MDL-b.
 *
 * - `latest`: mỗi phần tử của chuỗi được nâng lên bản **mới nhất cùng họ** mà
 *   lần quét gateway gần nhất thấy (và đã có giá) — lúc gọi, không sửa chuỗi đã
 *   lưu. Opus 5 trong chuỗi ⇒ gọi Opus 5.5 khi gateway có; Opus 5 lùi làm dự phòng.
 * - `pinned`: gọi đúng chuỗi đã lưu. Quét hằng ngày thấy bản mới thì **báo**
 *   (thông báo + badge), người dùng tự quyết.
 */
export const AiModelMode = z.enum(['latest', 'pinned']);
export const UpdateAiModelModeInput = z.object({ mode: AiModelMode });
/**
 * Kết quả lần quét gateway gần nhất của một key — cột `ai_provider_key.model_scan`.
 *
 * `newer` = bản mới hơn cùng họ của một phần tử trong chuỗi mà chuỗi chưa có.
 * `notified` = đã báo (không báo lại); `acknowledged` = người dùng bấm "Bỏ qua".
 * Lỗi mạng của một lần quét nằm ở `error`, và `available` giữ danh sách của lần
 * quét **thành công** gần nhất — một ngày gateway chập chờn không được xoá mất
 * đường nâng bản của chế độ `latest`.
 */
export const AiModelScan = z.object({
    scannedAt: z.iso.datetime(),
    available: z.array(z.string().max(200)).max(1000),
    newer: z.array(z.string().max(200)).max(100),
    notified: z.array(z.string().max(200)).max(500),
    acknowledged: z.array(z.string().max(200)).max(500),
    error: z.string().max(300).nullable(),
    /** Đề xuất model + effort cho từng loại việc (APM-MDL-d). Thiếu ở lần quét cũ. */
    recommendations: z.array(AiTierRecommendation).max(3).optional(),
    /** Khoá của bộ đề xuất đã báo — đổi đề xuất mới báo lại. */
    recommendationNotified: z.string().max(600).nullable().optional(),
    /** Model của gateway **chưa ghép được** benchmark công khai nào — nói ra, không đoán. */
    unmatched: z.array(z.string().max(200)).max(200).optional(),
});
/** Phần của lần quét mà giao diện cần: có gì mới chưa xử lý, quét lúc nào, lỗi gì. */
export const AiModelUpdates = z.object({
    newer: z.array(z.string()),
    scannedAt: z.iso.datetime().nullable(),
    error: z.string().nullable(),
    /** Đề xuất model + effort cho việc khó / hằng ngày / dễ — APM-MDL-d. */
    recommendations: z.array(AiTierRecommendation),
    unmatched: z.array(z.string()),
});
export const AiProviderKeyView = z.object({
    id: z.uuid(),
    provider: AiProvider,
    label: z.string(),
    /** `sk-ant-…7f3a` — bốn ký tự cuối là tất cả những gì được lộ ra. */
    keyLast4: z.string().length(4),
    /** `null` với `anthropic`. */
    baseUrl: z.string().nullable(),
    /** Chuỗi model ưu tiên cho từng tầng. Rỗng = theo cấu hình của tổ chức. */
    models: AiModelChains,
    /** APM-MDL-b. */
    modelMode: AiModelMode,
    /** Bản mới cùng họ chưa có trong chuỗi, chưa bị bỏ qua — nguồn của badge. */
    modelUpdates: AiModelUpdates,
    /**
     * Chuỗi **thật sự** sẽ gọi. Với `latest` khác `models` (bản mới nhất đứng
     * trước bản đã lưu); với `pinned` trùng `models`.
     */
    resolvedModels: AiModelChains,
    isActive: z.boolean(),
    verifiedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * Ba tầng model, chọn **theo việc** — APM-805.
 *
 * Ba nhóm việc trong repo này khác hẳn nhau về yêu cầu, nên bắt tất cả dùng một
 * model là hoặc trả quá nhiều tiền cho việc dễ, hoặc dùng model quá yếu cho
 * việc khó:
 *
 * - `strong` — sinh WBS, phân tích ảnh hưởng CR: output có cấu trúc dài, cần
 *   tool use tốt.
 * - `standard` — diễn đạt tín hiệu rủi ro, nhận xét CR: ngắn, dữ liệu đã có sẵn.
 * - `small` — phân loại và trích xuất: chạy nhiều, cần rẻ.
 *
 * Tầng khai ở `AiSkill`, còn **model cụ thể của từng tầng là cấu hình**
 * (`ai.model.strong` / `ai.default_model` / `ai.model.small`): danh sách model
 * phụ thuộc gateway của từng tổ chức, nên hardcode tên model vào skill là buộc
 * sửa code mỗi lần đổi gateway.
 */
export const AiModelTier = z.enum(['strong', 'standard', 'small']);
export const AiKeyTestResult = z.object({
    ok: z.boolean(),
    /** Số model mà key này nhìn thấy — bằng chứng key sống, không chỉ đúng dạng. */
    modelCount: z.number().int().nonnegative().optional(),
    /**
     * Model gateway trả về, kèm cờ dùng được và cờ đã có giá — APM-805.
     *
     * Có mặt vì với một gateway LiteLLM, "key sống" chưa đủ: người dùng còn phải
     * biết **gọi được model nào**. Cắt bớt nếu quá dài (xem `AI_MODEL_LIST_LIMIT`)
     * — một gateway có thể khai hàng trăm model, và một danh sách như vậy không
     * đọc được trên màn hình.
     */
    models: z.array(AiModelOption).optional(),
    /**
     * Chuỗi ưu tiên **đề xuất** cho ba tầng, suy từ chính danh sách trên.
     *
     * Có gợi ý vì tên model ở một gateway không tự nói lên nó mạnh hay yếu:
     * `deepseek-v4-flash` và `gemini-flash-lite-latest` đều có chữ "flash" nhưng
     * một cái là bản rẻ của model mạnh, cái kia là model nhỏ. Người dùng vẫn sửa
     * được — gợi ý là điểm bắt đầu, không phải quyết định.
     */
    suggestions: AiModelChains.optional(),
    /** Số model bị cắt khỏi `models`. `0` = danh sách đầy đủ. */
    modelsTruncated: z.number().int().nonnegative().optional(),
    error: z.string().optional(),
});
/**
 * Trần số tên model trả về cho giao diện.
 *
 * Không phải để tiết kiệm băng thông: một danh sách 300 dòng trên màn hình cấu
 * hình là một danh sách không ai đọc, và nó đẩy phần quan trọng (nút kích hoạt)
 * xuống dưới nếp gấp. Số bị cắt vẫn được nói ra.
 */
export const AI_MODEL_LIST_LIMIT = 60;
/**
 * Một dòng giá **có hiệu lực trong một khoảng thời gian**.
 *
 * `effectiveTo = null` nghĩa là còn hiệu lực. Đây là lý do bảng này tồn tại
 * thay vì một hằng số trong code: giá đổi theo thời gian, và usage của tháng
 * trước phải được tính bằng giá của tháng trước — nếu không thì mọi báo cáo chi
 * phí lịch sử đều sai sau lần đầu tiên Anthropic đổi bảng giá.
 */
export const AiModelPriceView = z.object({
    id: z.uuid(),
    modelId: z.string(),
    /** USD cho 1 triệu token. Chuỗi thập phân, không phải number — xem ghi chú dưới. */
    inputPerMTokUsd: z.string(),
    outputPerMTokUsd: z.string(),
    cacheReadMultiplier: z.string(),
    cacheWrite5mMultiplier: z.string(),
    cacheWrite1hMultiplier: z.string(),
    batchMultiplier: z.string(),
    effectiveFrom: z.iso.datetime(),
    effectiveTo: z.iso.datetime().nullable(),
});
const DecimalUsd = z
    .string()
    .trim()
    .regex(/^\d+(\.\d+)?$/, 'Số thập phân không âm, ví dụ 1.25');
/**
 * Lên lịch giá mới cho một model — APM-301, form ở tab Model từ APM-AIP.
 *
 * Model **chưa có** dòng giá thì đây là dòng đầu tiên: không có nó, `AiGateway`
 * bỏ qua model đó (luật #4). Model đã có giá thì dòng cũ được đóng ở
 * `effectiveFrom`, không sửa tại chỗ.
 */
export const AiPriceChangeInput = z.object({
    modelId: z.string().trim().min(1).max(60),
    inputPerMTokUsd: DecimalUsd,
    outputPerMTokUsd: DecimalUsd,
    /**
     * Giá đọc cache so với giá input. Bỏ trống ⇒ kế thừa dòng cũ, không có dòng
     * cũ thì mặc định của bảng (0.1, đúng cho Anthropic và OpenAI; Gemini 2.5 là
     * 0.25 — khác nhau theo nhà nên mới có ô này).
     */
    cacheReadMultiplier: DecimalUsd.optional(),
    effectiveFrom: z.iso.datetime(),
});
/**
 * Số tiền đi qua ranh giới FE↔BE dưới dạng **chuỗi thập phân**, không phải
 * `number`.
 *
 * `JSON.parse` biến mọi số thành double. Một hoá đơn cộng dồn từ hàng triệu
 * dòng log sẽ trôi dần — và trôi theo hướng không ai kiểm được. Frontend hiển
 * thị bằng `Intl.NumberFormat` trên chuỗi, không tự cộng.
 */
export const UsdAmount = z.string().regex(/^-?\d+(\.\d+)?$/, 'Số tiền phải là chuỗi thập phân');
export const AiCostBreakdown = z.object({
    modelId: z.string(),
    inputUsd: UsdAmount,
    outputUsd: UsdAmount,
    cacheReadUsd: UsdAmount,
    cacheWriteUsd: UsdAmount,
    totalUsd: UsdAmount,
    /** Dòng giá đã dùng — để đối chiếu khi ai đó thắc mắc về hoá đơn. */
    pricingId: z.uuid(),
});
/**
 * Tính năng gọi AI. Enum chứ không phải chuỗi tự do: `ai_usage_log.feature` là
 * cột dùng để rollup chi phí theo tính năng (docs/data-model.md §6), và chuỗi
 * tự do sẽ sinh ra `generate_wbs`, `generateWBS`, `wbs` trong cùng một báo cáo.
 */
export const AiFeature = z.enum([
    'assistant_chat',
    'ping',
    'wbs_generate',
    'risk_scan',
    'document_summarize',
    'raid_extract',
    'change_impact',
    'replay_narrate',
    'estimate_suggest',
    /** Xếp lại lịch khi có rủi ro — APM-1313. */
    'reschedule',
    /** Tóm tắt một task và luồng bình luận dài — APM-AI-g. */
    'task_summarize',
    /** Phân loại nhanh lúc tạo task — APM-AI-h. */
    'task_triage',
    /** Ước lượng story point cho việc đang gõ trong form — APM-AIA-a. */
    'story_point_estimate',
    /** Chọn người làm cho một việc trong form — APM-AIA-c. */
    'task_auto_assign',
    /** Tra lại một quyết định cũ — APM-AI-i. */
    'decision_archaeology',
    /** Suy ra phụ thuộc còn thiếu — APM-AI-j. */
    'dependency_infer',
    /** Bản bàn giao khi một người nghỉ dài — APM-AI-k. */
    'handover_brief',
    /** Thao tác hàng loạt bằng lời — APM-AI-l. */
    'bulk_edit',
    /** Replay giả định — APM-AI-t. */
    'what_if',
    /** Giải thích điểm rủi ro trễ — APM-AI-r. */
    'delay_risk_score',
    /** Đàm phán phạm vi — APM-AI-u. */
    'scope_negotiation',
    /** Ước lượng ngân sách — APM-AI-p. */
    'budget_estimate',
    /** Chọn người cho từng hạng mục của kế hoạch nháp — APM-UX-ai4. */
    'plan_assign',
    /** Mô tả màn hình thiết kế từ ảnh + dàn ý — APM-DC-h. */
    'design_describe',
    /** Thuyết minh thay đổi giữa hai bản chụp thiết kế — APM-DC-i. */
    'design_narrate',
    /** Soạn ghi chú phát hành của một bản (tag) cho người không đọc code — APM-GIT-l. */
    'git_release_notes',
    /** Sinh test case từ những việc đã làm — APM-TEST-ai. */
    'test_case_generate',
    /** Dò cột của file test case không theo mẫu — APM-TIMP. */
    'test_case_import_map',
    /** Gợi ý mục tiêu quý cho nhân sự — APM-PERF2-m. */
    'performance_goal_suggest',
    /** Tóm tắt công việc của team theo kỳ — APM-TWR-d. Lượt chạy không thuộc dự án nào. */
    'team_work_summary',
    /** Trợ lý tự tổng hợp hiệu suất đội 7 ngày từ số liệu đã tính sẵn — APM-CHAT2-c. Không thuộc dự án nào. */
    'team_performance_summary',
    /** Lời khuyên cấu hình model cho ba loại việc — APM-MDL-e. */
    'model_advice',
    /** Kế hoạch căn chỉnh định dạng tài liệu cá nhân — APM-CD-g. Không thuộc dự án nào. */
    'document_format',
    /** Sơ đồ sinh từ đoạn chữ đã bôi trong tài liệu cá nhân — APM-DRW-g. Không thuộc dự án nào. */
    'document_diagram',
    /** Kế hoạch định dạng đoạn vừa dán theo ngữ cảnh chỗ chèn — APM-SP-d. Không thuộc dự án nào. */
    'document_paste',
    /** Biểu đồ đề xuất từ một bảng (Data insight) trong tài liệu cá nhân — APM-CH-f. Không thuộc dự án nào. */
    'document_chart',
    /** Slide viết từ một vùng ngăn của tài liệu cá nhân — APM-SLD-g. Không thuộc dự án nào. */
    'document_slides',
    /** Viết chi tiết một mục của tài liệu cá nhân thành đề xuất — APM-SWR. Không thuộc dự án nào. */
    'document_write',
    /** Mô tả một ảnh vừa chèn vào tài liệu cá nhân (vision, một lần) — APM-SLD-n. Không thuộc dự án nào. */
    'document_image_describe',
    /** AI chỉ chủ thể (hộp + điểm) cho hộp Xoá nền ảnh — APM-BGR-d. Không thuộc dự án nào. */
    'document_image_background',
    /** Nhập PDF / `.docx` vào tài liệu cá nhân: LaTeX cho vùng công thức, phân xử hình / chữ — APM-CDIMP-f. Không thuộc dự án nào. */
    'document_import',
    /** Chuyển tệp Office thành `.chilldoc`: công thức Word còn lại → LaTeX, số hàng tiêu đề sheet — APM-CVT-h. Không thuộc dự án nào. */
    'document_convert',
    /** Soạn sổ chốt phương án từ tài liệu dự án — APM-DSHEET-g. */
    'decision_sheet_draft',
    /** Trích sổ chốt phương án từ văn bản / HTML dán vào — APM-DSHEET-h. */
    'decision_sheet_extract',
    /** Tóm tắt một MR / PR của tab Git (ba câu, khoá theo `head_sha`) — APM-GIT-m. */
    'git_cr_summarize',
    /** Đối chiếu tiêu chí của một việc với MR đã gắn — APM-GIT-n. */
    'git_task_coverage',
    /** Rà commit đã báo cho một mục checklist / việc hoàn thành có khớp không (chạy ngầm) — APM-PCRV. */
    'git_progress_review',
    /** Dịch một khối của tài liệu `.chilldoc` ở chế độ Song ngữ — APM-BIL-a. Không thuộc dự án nào. */
    'document_translate',
    /** Nhận ra thể loại tài liệu để chọn văn phong dịch — APM-BIL-a. Không thuộc dự án nào. */
    'document_genre_detect',
    /** Đề xuất / gộp bản prompt ghi đè cho một skill từ cây bằng chứng — APM-H-aq. Không thuộc dự án nào. */
    'prompt_propose',
]);
/**
 * Lời khuyên cấu hình model của AI — APM-MDL-e (skill `recommend_model_config`).
 * **Chỉ là gợi ý** (luật #6): áp dụng vẫn là người dùng bấm lưu chuỗi + effort.
 */
export const AiModelAdvice = z.object({
    recommendations: z
        .array(z.object({
        tier: z.enum(['strong', 'standard', 'small']),
        modelId: z.string().trim().min(1).max(200),
        reasoning: z.enum(['off', 'low', 'medium', 'high', 'adaptive']).nullable(),
        /** Vì sao — phải nêu điểm và giá lấy từ tool, không từ trí nhớ của model. */
        reason: z.string().trim().min(1).max(600),
    }))
        .max(3),
    /** Một đoạn cho người đọc: đánh đổi độ chính xác ↔ chi phí của cấu hình đề xuất. */
    summary: z.string().trim().min(1).max(1200),
});
export const ModelAdviceInput = z.object({
    /** Mong muốn thêm ("rẻ nhất có thể", "việc khó phải chắc"). Là **dữ liệu**, không phải chỉ dẫn. */
    prompt: z.string().trim().max(1000).optional(),
    /** Mã dấu vết do client sinh để mở SSE trước khi POST — APM-1302. */
    runId: z.uuid().optional(),
});
export const ModelAdviceResult = z.object({
    advice: AiModelAdvice,
    runId: z.uuid(),
    costUsd: z.string(),
    /** Lượt chạy chạm trần rồi nộp từ dữ liệu đã đọc (APM-H-p). */
    partial: z.boolean(),
});
export const AiCallStatus = z.enum([
    'success',
    /** `stop_reason: refusal` — model từ chối. Vẫn tốn token, vẫn phải ghi log. */
    'refused',
    /** Lỗi từ Anthropic hoặc mạng. Có thể tốn token nếu đứt giữa chừng. */
    'error',
    /** Bị hạn mức chặn trước khi gọi. Không tốn token, nhưng phải đếm được. */
    'blocked',
]);
export const AiBudgetPeriod = z.enum(['day', 'month']);
export const AiBudgetInput = z.object({
    /** Bỏ trống = hạn mức của cả tổ chức; có = hạn mức riêng của một người. */
    userId: z.uuid().optional(),
    period: AiBudgetPeriod,
    /** Chạm ngưỡng này thì cảnh báo, vẫn cho chạy. */
    softLimitUsd: UsdAmount,
    /** Chạm ngưỡng này thì **chặn**. Đây là thứ đứng giữa một vòng lặp lỗi và hoá đơn thật. */
    hardLimitUsd: UsdAmount,
});
export const AiBudgetView = z.object({
    id: z.uuid(),
    orgId: z.uuid(),
    userId: z.uuid().nullable(),
    period: AiBudgetPeriod,
    softLimitUsd: UsdAmount,
    hardLimitUsd: UsdAmount,
    /** Đã tiêu trong kỳ hiện tại. */
    spentUsd: UsdAmount,
    /** `true` khi đã vượt soft limit — UI hiện cảnh báo. */
    softExceeded: z.boolean(),
    hardExceeded: z.boolean(),
});
/** Kết quả `/admin/ai/ping` — gọi thật một lời gọi nhỏ nhất có thể. */
export const AiPingResult = z.object({
    ok: z.boolean(),
    modelId: z.string(),
    latencyMs: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: UsdAmount,
    reply: z.string(),
    error: z.string().optional(),
});
/**
 * Báo cáo lưu lượng AI — APM-303.
 *
 * ⚠️ Bốn loại token giữ nguyên tách biệt lên tới tận đây. Gộp lại ở bước báo
 * cáo thì tỉ lệ cache hit không tính được nữa — mà đó chính là chỉ số nói cho
 * admin biết prompt caching có đang hoạt động hay không, và nó là khoản tiết
 * kiệm lớn nhất có thể có (~90% input token).
 */
export const AiTokenTotals = z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    cacheReadInputTokens: z.number().int().nonnegative(),
    cacheCreationInputTokens: z.number().int().nonnegative(),
});
export const AiUsageTotals = AiTokenTotals.extend({
    costUsd: UsdAmount,
    callCount: z.number().int().nonnegative(),
    successCount: z.number().int().nonnegative(),
    refusedCount: z.number().int().nonnegative(),
    errorCount: z.number().int().nonnegative(),
    /** Bị hạn mức chặn. Con số này tăng đều là dấu hiệu cần nâng hạn mức — hoặc có vòng lặp lỗi. */
    blockedCount: z.number().int().nonnegative(),
    avgLatencyMs: z.number().nonnegative(),
    /**
     * `cache_read / (input + cache_read)`. Bao nhiêu phần token đầu vào được phục
     * vụ từ cache — càng cao càng rẻ.
     */
    cacheHitRate: z.number().min(0).max(1),
});
export const AiUsageBreakdownRow = AiUsageTotals.extend({
    /** Khoá của nhóm: id người dùng/dự án, hoặc tên feature/model. */
    key: z.string(),
    label: z.string(),
});
export const AiUsageSeriesPoint = AiUsageTotals.extend({
    /** `YYYY-MM-DD` theo UTC. */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const AiUsageReport = z.object({
    from: z.string(),
    to: z.string(),
    totals: AiUsageTotals,
    series: z.array(AiUsageSeriesPoint),
    byFeature: z.array(AiUsageBreakdownRow),
    byModel: z.array(AiUsageBreakdownRow),
    byUser: z.array(AiUsageBreakdownRow),
    byProject: z.array(AiUsageBreakdownRow),
    /** Hạn mức hiện tại kèm số đã tiêu, để vẽ chi phí so với ngân sách. */
    budgets: z.array(AiBudgetView),
    /**
     * Ngày trong khoảng **chưa** được rollup, đang đọc trực tiếp từ bảng raw.
     *
     * Bày ra thay vì giấu: nếu danh sách này dài hơn một ngày thì job rollup đã
     * chết, và báo cáo tuy vẫn đúng số nhưng đang chạy chậm dần.
     */
    liveDays: z.array(z.string()),
});
//# sourceMappingURL=ai.js.map